import React, { useState, useMemo } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toYMD, todayYMD, startOfWeek } from '@/utils/taskDates';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/**
 * Compact month calendar. Dots mark days with tasks. Clicking a day jumps the
 * weekly board to the week containing that date. `dayCounts` is { 'YYYY-MM-DD': n }.
 */
const MiniCalendar = ({ viewMonth, dayCounts, selectedWeekStart, onPickDate, onMonthChange }) => {
  const [cursor, setCursor] = useState(() => viewMonth || new Date());

  const changeMonth = (delta) => {
    const next = new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1);
    setCursor(next);
    onMonthChange && onMonthChange(next);
  };

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7; // Monday-based offset
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < lead; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return arr;
  }, [cursor]);

  const today = todayYMD();
  const weekStartYMD = selectedWeekStart ? toYMD(selectedWeekStart) : null;
  const inSelectedWeek = (d) => {
    if (!weekStartYMD) return false;
    return toYMD(startOfWeek(d)) === weekStartYMD;
  };

  return (
    <div className="select-none">
      <div className="flex items-center justify-between px-1 mb-1.5">
        <span className="text-sm font-bold text-[#002169]">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</span>
        <div className="flex items-center gap-0.5">
          <button onClick={() => changeMonth(-1)} className="text-gray-400 hover:text-[#002169] p-0.5"><ChevronLeftIcon style={{ fontSize: 18 }} /></button>
          <button onClick={() => changeMonth(1)}  className="text-gray-400 hover:text-[#002169] p-0.5"><ChevronRightIcon style={{ fontSize: 18 }} /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DOW.map((d, i) => <div key={i} className="text-[10px] font-semibold text-gray-400 text-center">{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ymd = toYMD(d);
          const isToday = ymd === today;
          const count = dayCounts?.[ymd] || 0;
          const selectedWeek = inSelectedWeek(d);
          return (
            <button key={i} onClick={() => onPickDate(d)}
              className={`relative aspect-square flex items-center justify-center rounded-lg text-xs transition
                ${isToday ? 'bg-[#002169] text-white font-bold'
                  : selectedWeek ? 'bg-blue-100 text-[#002169] font-semibold'
                  : 'text-gray-600 hover:bg-gray-100'}`}>
              {d.getDate()}
              {count > 0 && (
                <span className={`absolute bottom-0.5 w-1 h-1 rounded-full ${isToday ? 'bg-white' : 'bg-amber-500'}`} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MiniCalendar;
